import { css, styled } from "goober";
import { useEffect, useRef } from "react";
import {
  desktopViewportFrameCss,
  useDesktopViewportScale,
} from "../../hooks/useDesktopViewport";
import { CircleMesh } from "../CircleMesh";
import { HtmlViewer, type HtmlViewerHandle } from "../HtmlViewer";
import type { FileRenderControl } from "./types";

const StyledHtml = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const bleedCss = (bleed: number) => css`
  margin: -${bleed}px;
  width: calc(100% + ${bleed * 2}px);
  height: calc(100% + ${bleed * 2}px);
`;

const Background = styled("div")`
  position: absolute;
  inset: 0;
`;

const PreviewContainer = styled("div")`
  position: relative;
  z-index: 1;
  width: 80%;
  max-width: 600px;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

const frame = css`
  ${desktopViewportFrameCss}
  display: block;
  border: none;
  background: transparent;
`;

// The interactive variant is a plain browser-like viewport: the page fills the
// panel at its own width and scrolls natively, rather than being scaled down
// into the preview card.
const InteractiveContainer = styled("div")`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: white;
`;

const interactiveFrame = css`
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

type Props = {
  output: string;
  bleed?: number;
  baseRoute?: string;
  interactive?: boolean;
  onControls?: (controls: FileRenderControl[]) => void;
};

export function Html({
  output,
  bleed,
  baseRoute,
  interactive,
  onControls,
}: Props) {
  const previewContainerRef = useDesktopViewportScale<HTMLDivElement>();
  const viewerRef = useRef<HtmlViewerHandle>(null);

  useEffect(() => {
    if (!interactive || !onControls) return;
    onControls([
      {
        id: "back",
        icon: "arrow-left",
        label: "Back",
        active: false,
        onToggle: () => viewerRef.current?.back(),
      },
      {
        id: "forward",
        icon: "arrow-right",
        label: "Forward",
        active: false,
        onToggle: () => viewerRef.current?.forward(),
      },
      {
        id: "reload",
        icon: "rotate-cw",
        label: "Reload",
        active: false,
        onToggle: () => viewerRef.current?.refresh(),
      },
    ]);
    return () => onControls([]);
  }, [interactive, onControls]);

  if (interactive) {
    return (
      <InteractiveContainer className={bleed ? bleedCss(bleed) : undefined}>
        <HtmlViewer
          apiRef={viewerRef}
          html={output}
          className={interactiveFrame}
          baseRoute={baseRoute}
          interactive
        />
      </InteractiveContainer>
    );
  }

  return (
    <StyledHtml className={bleed ? bleedCss(bleed) : undefined}>
      <Background>
        <CircleMesh mutingIntensity={1} seed="html-preview" />
      </Background>
      <PreviewContainer ref={previewContainerRef}>
        <HtmlViewer html={output} className={frame} baseRoute={baseRoute} />
      </PreviewContainer>
    </StyledHtml>
  );
}
